import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Clock, CheckCircle2, XCircle, Sparkles, MessageCircle } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/api";

const STATUS_META = {
    pending: { icon: Clock, color: "bg-brand-gold text-white", key: "status_pending" },
    confirmed: { icon: CheckCircle2, color: "bg-brand-emerald text-white", key: "status_confirmed" },
    completed: { icon: CheckCircle2, color: "bg-brand-navy text-white", key: "status_completed" },
    cancelled: { icon: XCircle, color: "bg-gray-400 text-white", key: "status_cancelled" },
};

export default function BookingTrack() {
    const { token } = useParams();
    const { t, lang } = useI18n();
    const [b, setB] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/bookings/track/${token}`)
            .then((r) => setB(r.data))
            .catch(() => setB(null))
            .finally(() => setLoading(false));
    }, [token]);

    const copy = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success(t("track.link_copied"));
    };

    if (loading) return <PublicLayout><div className="text-center py-32 text-gray-500">Chargement…</div></PublicLayout>;
    if (!b) return <PublicLayout><div className="text-center py-32 text-gray-500" data-testid="track-not-found">{t("track.not_found")}</div></PublicLayout>;

    const meta = STATUS_META[b.status] || STATUS_META.pending;
    const Icon = meta.icon;

    return (
        <PublicLayout>
            <section className="bg-brand-navy text-white" data-testid="track-hero">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
                    <div className="inline-flex items-center gap-3 text-brand-gold text-[0.7rem] tracking-[0.3em] uppercase font-semibold mb-3">
                        <Sparkles className="w-3.5 h-3.5" /> {t("track.title")}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl">
                        #{b.id.slice(0, 8).toUpperCase()}
                    </h1>
                </div>
            </section>
            <section className="py-16 bg-brand-paper">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6" data-testid="track-panel">
                    <div className={`p-8 flex items-center gap-5 ${meta.color}`} data-testid="track-status">
                        <div className="w-14 h-14 bg-white/15 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-6 h-6" strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold opacity-80">{t("track.title")}</div>
                            <div className="font-display text-2xl mt-1">{t(`track.${meta.key}`)}</div>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-100 p-6 space-y-3 text-sm">
                        <Row k="Type" v={b.type === "meeting" ? t("bookings_admin.meeting") : t("bookings_admin.rental")} />
                        <Row k={t("booking.name")} v={b.name} />
                        <Row k={t("booking.email")} v={b.email} />
                        {b.phone ? <Row k={t("booking.phone")} v={b.phone} /> : null}
                        {b.type === "rental" ? (
                            <>
                                <Row k={t("booking.rental_property")} v={b.property_title || b.property_id} />
                                <Row k={t("booking.rental_check_in")} v={b.check_in} />
                                <Row k={t("booking.rental_check_out")} v={b.check_out} />
                                <Row k={t("booking.rental_guests")} v={b.guests} />
                            </>
                        ) : (
                            <>
                                <Row k="Date" v={b.meeting_date} />
                                <Row k="Créneau" v={`${b.meeting_start} – ${b.meeting_end}`} />
                            </>
                        )}
                        {b.message ? <Row k={t("booking.message")} v={b.message} /> : null}
                    </div>

                    <button
                        onClick={copy}
                        data-testid="track-copy"
                        className="inline-flex items-center gap-2 px-5 py-3 bg-brand-navy hover:bg-brand-navy-soft text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Copy className="w-4 h-4" /> {t("track.copy_link")}
                    </button>
                </div>
            </section>
        </PublicLayout>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
            <span className="text-gray-500 uppercase text-[0.62rem] tracking-[0.2em] font-semibold min-w-[130px]">{k}</span>
            <span className="text-brand-navy text-right flex-1">{v || "—"}</span>
        </div>
    );
}
